import CloseIcon from '@mui/icons-material/Close';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Popover from '@mui/material/Popover';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import Typography from '@mui/material/Typography';
import _ from 'lodash';
import { HTMLAttributes, ReactNode, useMemo, useState } from 'react';

import { useAppDispatch, useAppSelector } from 'redux/hooks';
import { addCard, updateCard } from 'redux/slice/datasetCardControllerSlice';

import CustomLink from 'components/common/CustomLink';
import { useConstants } from '../../../utils/hooks/useConstants';

// =============================================================================

const PARAMETER_TYPES = [
  'WATER_PROPERTIES',
  'WATER_CONTENTS',
  'AIR_PROPERTIES',
  'MONTHLY_CRUISE_PARAMETERS',
  'WAVE_AND_TIDE',
  'HYDRODYNAMICS'
];

type TabPanelProps = {
  children: ReactNode;
  value: number;
  setDirection?: boolean;
  index: number;
  mobile?: boolean;
} & HTMLAttributes<HTMLDivElement>;

const TabPanel = (props: TabPanelProps) => {
  const { children, value, setDirection, index, mobile, ...other } = props;

  return (
    <div hidden={value !== index} {...other}>
      {value === index && (
        <Box
          sx={{
            p: mobile ? 0 : 3,
            display: 'flex',
            ...(setDirection && { flexDirection: 'column' })
          }}
        >
          {children}
        </Box>
      )}
    </div>
  );
};

// =============================================================================
interface PopoverOptionsProps {
  anchorEl: any;
  onClose: any;
  anchorPosition?: { top: number; left: number };
  setAnchorEl: any;
  mode: 'addCard' | 'updateCard';
  cardIndex?: string;
  location?: string;
  mobile?: boolean;
}

export default function PopoverOptions(props: PopoverOptionsProps) {
  const open = Boolean(props.anchorEl);

  return (
    <Popover
      open={open}
      anchorReference={props.anchorPosition ? 'anchorPosition' : 'anchorEl'}
      anchorPosition={props.anchorPosition}
      anchorEl={props.anchorEl}
      onClose={props.onClose}
      anchorOrigin={{
        vertical: 'center',
        horizontal: 'right'
      }}
      sx={{
        '& .MuiPopover-paper': {
          boxShadow: `0px 8px 10px rgba(0, 0, 0, 0.16)`,
          borderRadius: '16px',
          backgroundColor: 'white.50'
        }
      }}
    >
      <PopoverView
        cardIndex={props.cardIndex}
        mode={props.mode}
        setAnchorEl={props.setAnchorEl}
        handleClose={props.onClose}
        location={props.location}
        mobile={props.mobile ?? false}
      />
    </Popover>
  );
}

// =============================================================================
type Parameter = {
  id: string;
  parameterName: string;
  category: string;
  parameterDesc: string;
  unit: string;
  isFree: boolean;
  location: string;
  isMonthlyAvg: boolean;
  noOfDecimalPlaces: number;
  depth: string[];
};

type StructuredParamsList = {
  [location: string]: {
    LIVE: { [category: string]: Parameter[] };
    MONTHLY: { [category: string]: Parameter[] };
    CRUISE: { [category: string]: Parameter[] };
  };
};

const popover_view_wrapper_style = {
  padding: '26px 16px 16px 16px'
};

interface PopoverViewProps {
  cardIndex?: string;
  setAnchorEl: any;
  mode: 'addCard' | 'updateCard';
  handleClose: () => void;
  location?: string;
  mobile: boolean;
}

const PopoverView = (props: PopoverViewProps) => {
  const { CONSTANTS } = useConstants();
  const locationMap = useMemo(() => {
    return Object.values(CONSTANTS.LOCATION_ACRONYM_TO_FULL_FORM_MAP);
  }, [CONSTANTS]);

  const [locationValue, setLocationValue] = useState(
    props.location ? locationMap.indexOf(props.location) : 0
  );
  const [divisionValue, setDivisionValue] = useState(0);
  const dispatch = useAppDispatch();
  const cardSet: any = useAppSelector(
    (state) => state.datasetCardController.set
  );

  const paramsData: Parameter[] | null = useAppSelector(
    (state) => state.parameters?.data
  );

  const handleLocationChange = (
    event: React.SyntheticEvent,
    newValue: number
  ) => {
    setLocationValue(newValue);
    setDivisionValue(0);
  };
  const handleDivisionChange = (
    event: React.SyntheticEvent,
    newValue: number
  ) => {
    setDivisionValue(newValue);
  };

  const getTabsAndTabPanels = () => {
    if (
      paramsData === null ||
      !Array.isArray(paramsData) ||
      paramsData.length === 0
    )
      return null;
    if (!paramsData) return null; // For lsp
    const locationBasedParamsWithPermissions = _.groupBy(
      paramsData,
      'location'
    );

    let locationBasedSubDividedParamsDummy: StructuredParamsList = {};

    for (const location in locationBasedParamsWithPermissions) {
      locationBasedSubDividedParamsDummy[location] = {
        LIVE: _.groupBy(
          locationBasedParamsWithPermissions[location].filter(
            (obj) =>
              obj.category !== 'MONTHLY_CRUISE_PARAMETERS' && !obj.isMonthlyAvg
          ),
          'category'
        ),
        MONTHLY: _.groupBy(
          locationBasedParamsWithPermissions[location].filter(
            (obj) =>
              obj.category !== 'MONTHLY_CRUISE_PARAMETERS' && obj.isMonthlyAvg
          ),
          'category'
        ),
        CRUISE: _.groupBy(
          locationBasedParamsWithPermissions[location].filter(
            (obj) => obj.category == 'MONTHLY_CRUISE_PARAMETERS'
          ),
          'category'
        )
      };
    }

    // To maintain location order in the UI
    let locationBasedSubDividedCategorizedParams: StructuredParamsList = {};
    Object.keys(CONSTANTS.LOCATION_ACRONYM_TO_FULL_FORM_MAP).map(
      (location: string) => {
        if (locationBasedSubDividedParamsDummy[location]) {
          locationBasedSubDividedCategorizedParams[location] =
            locationBasedSubDividedParamsDummy[location];
        }
      }
    );

    return (
      <>
        <Tabs
          variant='scrollable'
          scrollButtons
          allowScrollButtonsMobile
          value={locationValue}
          onChange={handleLocationChange}
        >
          {Object.keys(locationBasedSubDividedCategorizedParams).map(
            (location: any, idx: any) => (
              <Tab
                label={CONSTANTS.LOCATION_ACRONYM_TO_FULL_FORM_MAP[location]}
                key={`Tab-${idx}`}
              />
            )
          )}
        </Tabs>
        {Object.values(locationBasedSubDividedCategorizedParams).map(
          (sData: any, idx: any) => (
            <TabPanel
              value={locationValue}
              index={idx}
              key={`TabPanel-${idx}`}
              setDirection={true}
              mobile={props.mobile}
            >
              {
                <Tabs
                  variant='scrollable'
                  scrollButtons
                  allowScrollButtonsMobile
                  value={divisionValue}
                  onChange={handleDivisionChange}
                >
                  {Object.keys(sData).map((divided: any, sIdx: any) => (
                    <Tab
                      label={
                        divided == 'LIVE'
                          ? 'Sensor Live'
                          : divided == 'MONTHLY'
                            ? 'Monthly Averages'
                            : 'Cruise Data'
                      }
                      key={`Tab-${idx}-${sIdx}`}
                    />
                  ))}
                </Tabs>
              }
              {Object.values(sData).map((pData: any, pIdx: any) => (
                <TabPanel
                  value={divisionValue}
                  index={pIdx}
                  key={`TabPanel-${idx}-${pIdx}`}
                  setDirection={props.mobile}
                >
                  {PARAMETER_TYPES.map((type: any, index: number) =>
                    pData[type] ? (
                      <div
                        key={`type-${idx}-${index}`}
                        style={
                          props.mobile
                            ? {
                                textAlign: 'center'
                              }
                            : {
                                marginRight: 10
                              }
                        }
                      >
                        <Typography
                          variant='assistive_text'
                          color='gray.700'
                          style={{
                            ...(type == 'WATER_CONTENTS' && {
                              visibility: 'hidden'
                            })
                          }}
                        >
                          {type.replace(/_/g, ' ')}
                        </Typography>
                        <div style={{ height: 16 }} />
                        {pData[type].map((param: any, index: number) => (
                          <div
                            key={`param-${idx}-${index}`}
                            style={{ marginBottom: 16 }}
                          >
                            <CustomLink
                              variant='button'
                              color='blue.800'
                              onClick={() =>
                                props.mode === 'addCard'
                                  ? handleAddCard(param)
                                  : handleUpdateCard(param)
                              }
                            >
                              {param.parameterDesc}
                            </CustomLink>
                            &nbsp;&nbsp;
                            <Typography variant='button' color='gray.700'>
                              {param.unit === '' ? '' : `(${param.unit})`}
                            </Typography>
                          </div>
                        ))}
                      </div>
                    ) : null
                  )}
                </TabPanel>
              ))}
            </TabPanel>
          )
        )}
      </>
    );
  };

  return (
    <div style={props.mobile ? {} : popover_view_wrapper_style}>
      {props.mobile ? null : (
        <IconButton
          size='large'
          style={{ float: 'right' }}
          onClick={props.handleClose}
        >
          <CloseIcon sx={{ color: 'blue.800' }} />
        </IconButton>
      )}
      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          flexDirection: 'column'
        }}
      >
        {getTabsAndTabPanels()}
      </div>

      <div style={{ textAlign: 'center' }}>
        <Typography
          sx={{ width: '100%', textAlign: 'center' }}
          variant='caption'
          color='gray.700'
        >
          Select up to 2 parameters of different measurements
        </Typography>
      </div>
    </div>
  );

  function handleAddCard(option: any) {
    const parameterName = option.parameterName;
    const locationFF = locationMap[locationValue];

    if (
      cardSet[0] &&
      cardSet[0].location === option.location &&
      cardSet[0].isMonthlyAvg === option.isMonthlyAvg &&
      cardSet[0].parameterName === parameterName
    ) {
      alert(`${option.parameterDesc} for ${locationFF} was already added.`);
      return;
    }

    const isCruiseParam = option.category === 'MONTHLY_CRUISE_PARAMETERS';

    const depthOptions = isCruiseParam
      ? [CONSTANTS.DEPTH_MAP.SHALLOW]
      : Object.values(CONSTANTS.DEPTH_MAP);

    const depths = depthOptions.map((depth: string) => ({
      value: depth,
      text: CONSTANTS.DEPTH_ACRONYM_TO_FULL_FORM_MAP[depth]
    }));

    const newCardLayout = {
      ...option,
      location: CONSTANTS.LOCATION_ACRONYM_TO_FULL_FORM_MAP[option.location],
      isCruiseParam,
      depths,
      depth: depths[0]
    };

    dispatch(addCard(newCardLayout));
    props.setAnchorEl(null);
  }

  function handleUpdateCard(option: any) {
    const parameterName = option.parameterName;
    const locationFF = locationMap[locationValue];
    if (
      cardSet[0] &&
      cardSet[0].location === option.location &&
      cardSet[0].isMonthlyAvg === option.isMonthlyAvg &&
      cardSet[0].parameterName === parameterName
    ) {
      alert(`${option.parameterDesc} for ${locationFF} was already added.`);
      return;
    }

    if (
      cardSet[1] &&
      cardSet[1].location === option.location &&
      cardSet[1].isMonthlyAvg === option.isMonthlyAvg &&
      cardSet[1].parameterName === parameterName
    ) {
      alert(`${option.parameterDesc} for ${locationFF} was already added.`);
      return;
    }

    const isCruiseParam = option.category === 'MONTHLY_CRUISE_PARAMETERS';

    const depthOptions = isCruiseParam
      ? [CONSTANTS.DEPTH_MAP.SHALLOW]
      : Object.values(CONSTANTS.DEPTH_MAP);

    const depths = depthOptions.map((depth: string) => ({
      value: depth,
      text: CONSTANTS.DEPTH_ACRONYM_TO_FULL_FORM_MAP[depth]
    }));

    // @ts-ignore
    const updatedCardLayout = {
      ...option,
      location: CONSTANTS.LOCATION_ACRONYM_TO_FULL_FORM_MAP[option.location],
      isCruiseParam: option.category === 'MONTHLY_CRUISE_PARAMETERS',
      depths,
      depth: depths[0]
    };
    dispatch(updateCard({ index: props.cardIndex, data: updatedCardLayout }));
    props.setAnchorEl(null);
  }
};
